import React from 'react';
import { Html } from '../../helpers/konva/html';
import Konva from 'konva';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';

export interface TextEditorProps {
  textNodeRef: React.RefObject<any>;
  value: string;
  onBlur: () => void;
  onChange: (value: string) => void;
  onMouseEnter?: (e: Konva.KonvaEventObject<MouseEvent>) => void;
  onMouseLeave?: (e: Konva.KonvaEventObject<MouseEvent>) => void;
}

export const TextEditor: React.FC<TextEditorProps> = ({
  textNodeRef,
  value,
  onBlur,
  onChange,
  onMouseEnter,
  onMouseLeave
}) => {
  const [style, setStyle] = React.useState<React.CSSProperties>();
  React.useLayoutEffect(() => {
    const textNode = textNodeRef.current;
    // apply many styles to match text on canvas as close as possible
    // remember that text rendering on canvas and on the textarea can be different
    // and sometimes it is hard to make it 100% the same. But we will try...
    const newStyle: React.CSSProperties = {};
    newStyle.width = textNode.width() - textNode.padding() * 2 + 'px';
    newStyle.height = textNode.height() - textNode.padding() * 2 + 10 + 'px';
    newStyle.fontSize = textNode.fontSize() + 'px';
    newStyle.border = 'none';
    newStyle.padding = '0px';
    newStyle.overflow = 'hidden';
    newStyle.background = 'none';
    newStyle.outline = 'none';
    newStyle.resize = 'none';
    newStyle.lineHeight = textNode.lineHeight() + 0.01;
    newStyle.fontFamily = '"' + textNode.fontFamily() + '"';
    newStyle.transformOrigin = 'left top';
    newStyle.textAlign = textNode.align();
    newStyle.color = textNode.fill();
    newStyle.overflowWrap = 'break-word';
    newStyle.whiteSpace = 'normal';
    newStyle.userSelect = 'text';
    newStyle.wordBreak = 'normal';

    // Shallow compare to avoid unnecessary state updates
    const keys = Object.keys(newStyle) as (keyof React.CSSProperties)[];
    const hasChanged =
      !style ||
      keys.length !== Object.keys(style).length ||
      keys.some((k) => newStyle[k] !== style[k]);

    if (hasChanged) {
      setStyle(newStyle);
    }
  }, [textNodeRef, value]);

  const debounced = useDebouncedValue(value, onChange);

  const [textAreaReaRef, setTextAreaRef] =
    React.useState<HTMLTextAreaElement | null>(null);

  React.useEffect(() => {
    if (textAreaReaRef) {
      textAreaReaRef.focus();
    }
  }, [textAreaReaRef]);

  // Auto-grow the textarea to fit its content. Without this the textarea
  // would clip while typing (overflow: hidden) until the debounced onChange
  // fires and the parent shape grows on the next render.
  const autoGrow = React.useCallback((el: HTMLTextAreaElement) => {
    // Reset first so shrinks also recompute correctly when content is deleted.
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  React.useLayoutEffect(() => {
    if (textAreaReaRef) autoGrow(textAreaReaRef);
  }, [textAreaReaRef, debounced.value, style, autoGrow]);

  return (
    <Html>
      <textarea
        className="polotno-input"
        style={{
          ...style
        }}
        value={debounced.value}
        onChange={(e) => {
          debounced.onChange(e.target.value);
          autoGrow(e.currentTarget);
        }}
        onBlur={() => {
          debounced.flush();
          onBlur();
        }}
        onMouseEnter={onMouseEnter as never}
        onMouseLeave={onMouseLeave as never}
        ref={setTextAreaRef}
      />
    </Html>
  );
};
